import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { Link } from './link';
import { LoadingSpinner } from '../ui/loading-spinner';

const GLOW_COLORS: Record<string, string> = {
  primary: 'var(--color-ds-emerald-500)',
  red: 'var(--color-ds-red-500)',
  blue: 'var(--color-ds-blue-500)',
  amber: 'var(--color-ds-amber-600)',
  teal: 'var(--color-ds-teal-500)',
  purple: 'var(--color-ds-purple-500)',
  pink: 'var(--color-ds-pink-500)',
  dark: 'var(--color-foreground)',
  light: 'var(--color-foreground)',
  white: 'var(--color-foreground)',
  zinc: 'var(--color-ds-gray-500)',
  'dark/white': 'var(--color-foreground)',
};

const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-sm border',
    // Focus
    'focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary',
    // Active press
    'active:scale-[0.97] transition-transform duration-100',
    // Disabled
    'data-disabled:opacity-50 cursor-pointer',
    // Icon
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]',
  ],
  sizes: {
    sm: 'px-2 py-1 text-xs/5 *:data-[slot=icon]:size-3 *:data-[slot=icon]:my-0',
    default:
      'text-base/6 px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6 *:data-[slot=icon]:size-4 *:data-[slot=icon]:my-0.5',
    lg: 'px-4 py-2.5 text-base/6 *:data-[slot=icon]:size-5 *:data-[slot=icon]:my-0.5',
  },
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-(--btn-border)',
    // Dark mode: border is rendered on `after` so background is set to button background
    'dark:bg-(--btn-bg)',
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-sm)-1px)] before:bg-(--btn-bg)',
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    'before:shadow-sm',
    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    'dark:before:hidden',
    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-sm)-1px)]',
    // Inner highlight shadow
    'after:shadow-[inset_0_1px_--theme(--color-white/15%)]',
    // White overlay on hover
    'data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)',
    // Dark mode: `after` layer expands to cover entire button
    'dark:after:-inset-px dark:after:rounded-md',
    // Disabled
    'data-disabled:before:shadow-none data-disabled:after:shadow-none',
  ],
  tinted: [
    // Base
    'border-current/15 data-active:border-current/20 data-hover:border-current/20',
    // Icon inherits text color
    '[--btn-icon:currentColor]',
  ],
  tintedColors: {
    light:
      'bg-ds-gray-200/50 text-ds-gray-900 data-active:bg-ds-gray-200/70 data-hover:bg-ds-gray-200/70',
    'dark/white':
      'bg-ds-gray-200/50 text-ds-gray-900 data-active:bg-ds-gray-200/70 data-hover:bg-ds-gray-200/70',
    dark: 'bg-ds-gray-200/50 text-ds-gray-900 data-active:bg-ds-gray-200/70 data-hover:bg-ds-gray-200/70',
    white:
      'bg-ds-gray-200/50 text-ds-gray-900 data-active:bg-ds-gray-200/70 data-hover:bg-ds-gray-200/70',
    zinc: 'bg-ds-gray-200/50 text-ds-gray-900 data-active:bg-ds-gray-200/70 data-hover:bg-ds-gray-200/70',
    red: 'bg-ds-red-200/50 text-ds-red-900 data-active:bg-ds-red-200/70 data-hover:bg-ds-red-200/70',
    primary:
      'bg-ds-emerald-200/50 text-ds-emerald-900 data-active:bg-ds-emerald-200/70 data-hover:bg-ds-emerald-200/70 dark:bg-ds-emerald-400/30 dark:text-ds-emerald-900 dark:data-active:bg-ds-emerald-400/40 dark:data-hover:bg-ds-emerald-400/40',
    blue: 'bg-ds-blue-200/50 text-ds-blue-900 data-active:bg-ds-blue-200/70 data-hover:bg-ds-blue-200/70',
    amber:
      'bg-ds-amber-200/50 text-ds-amber-900 data-active:bg-ds-amber-200/70 data-hover:bg-ds-amber-200/70',
    teal: 'bg-ds-teal-200/50 text-ds-teal-900 data-active:bg-ds-teal-200/70 data-hover:bg-ds-teal-200/70',
    purple:
      'bg-ds-purple-200/50 text-ds-purple-900 data-active:bg-ds-purple-200/70 data-hover:bg-ds-purple-200/70',
    pink: 'bg-ds-pink-200/50 text-ds-pink-900 data-active:bg-ds-pink-200/70 data-hover:bg-ds-pink-200/70',
  },
  outline: [
    // Base
    'border-border text-foreground data-active:bg-accent data-hover:bg-accent',
    // Icon
    '[--btn-icon:var(--color-muted-foreground)] data-active:[--btn-icon:var(--color-foreground)] data-hover:[--btn-icon:var(--color-foreground)]',
  ],
  plain: [
    // Base
    'border-transparent text-foreground data-active:bg-accent data-hover:bg-accent',
    // Icon
    '[--btn-icon:var(--color-muted-foreground)] data-active:[--btn-icon:var(--color-foreground)] data-hover:[--btn-icon:var(--color-foreground)]',
  ],
  colors: {
    // Structural variants (grayscale, no chromatic color)
    light: [
      'text-ds-gray-1000 [--btn-bg:white] [--btn-border:var(--color-ds-gray-1000)]/10 [--btn-hover-overlay:var(--color-ds-gray-1000)]/2.5 data-active:[--btn-border:var(--color-ds-gray-1000)]/15 data-hover:[--btn-border:var(--color-ds-gray-1000)]/15',
      'dark:text-white dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-ds-gray-200)]',
      '[--btn-icon:var(--color-muted-foreground)] data-active:[--btn-icon:var(--color-foreground)] data-hover:[--btn-icon:var(--color-foreground)]',
    ],
    'dark/white': [
      'text-white [--btn-bg:var(--color-ds-gray-1000)] [--btn-border:var(--color-ds-gray-1000)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:text-ds-gray-100 dark:[--btn-bg:white] dark:[--btn-hover-overlay:var(--color-ds-gray-1000)]/5',
      '[--btn-icon:var(--color-ds-gray-600)] data-active:[--btn-icon:var(--color-ds-gray-500)] data-hover:[--btn-icon:var(--color-ds-gray-500)] dark:[--btn-icon:var(--color-ds-gray-500)] dark:data-active:[--btn-icon:var(--color-ds-gray-400)] dark:data-hover:[--btn-icon:var(--color-ds-gray-400)]',
    ],
    dark: [
      'text-white [--btn-bg:var(--color-ds-gray-1000)] [--btn-border:var(--color-ds-gray-1000)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-ds-gray-200)]',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    white: [
      'text-ds-gray-1000 [--btn-bg:white] [--btn-border:var(--color-ds-gray-1000)]/10 [--btn-hover-overlay:var(--color-ds-gray-1000)]/2.5 data-active:[--btn-border:var(--color-ds-gray-1000)]/15 data-hover:[--btn-border:var(--color-ds-gray-1000)]/15',
      'dark:text-ds-gray-100 dark:[--btn-hover-overlay:var(--color-ds-gray-1000)]/5',
      '[--btn-icon:var(--color-ds-gray-600)] data-active:[--btn-icon:var(--color-ds-gray-700)] data-hover:[--btn-icon:var(--color-ds-gray-700)] dark:[--btn-icon:var(--color-ds-gray-500)] dark:data-active:[--btn-icon:var(--color-ds-gray-400)] dark:data-hover:[--btn-icon:var(--color-ds-gray-400)]',
    ],
    zinc: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-ds-gray-700)] [--btn-border:var(--color-ds-gray-800)]/90',
      'dark:[--btn-hover-overlay:var(--color-white)]/5',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    // Design system chromatic colors
    red: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-ds-red-700)] [--btn-border:var(--color-ds-red-800)]/90',
      '[--btn-icon:var(--color-white)]/70 data-active:[--btn-icon:var(--color-white)] data-hover:[--btn-icon:var(--color-white)]',
    ],
    primary: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-ds-emerald-700)] [--btn-border:var(--color-ds-emerald-800)]/90',
      'dark:[--btn-bg:var(--color-ds-emerald-500)] dark:!border-white/10',
      '[--btn-icon:var(--color-white)]/70 data-active:[--btn-icon:var(--color-white)] data-hover:[--btn-icon:var(--color-white)]',
    ],
    blue: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-ds-blue-800)] [--btn-border:var(--color-ds-blue-900)]/90',
      '[--btn-icon:var(--color-white)]/70 data-active:[--btn-icon:var(--color-white)] data-hover:[--btn-icon:var(--color-white)]',
    ],
    amber: [
      'text-ds-amber-1000 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-ds-amber-600)] [--btn-border:var(--color-ds-amber-700)]/80',
      '[--btn-icon:currentColor]',
    ],
    teal: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-ds-teal-700)] [--btn-border:var(--color-ds-teal-800)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    purple: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-ds-purple-700)] [--btn-border:var(--color-ds-purple-800)]/90',
      '[--btn-icon:var(--color-white)]/70 data-active:[--btn-icon:var(--color-white)] data-hover:[--btn-icon:var(--color-white)]',
    ],
    pink: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-ds-pink-700)] [--btn-border:var(--color-ds-pink-800)]/90',
      '[--btn-icon:var(--color-white)]/70 data-active:[--btn-icon:var(--color-white)] data-hover:[--btn-icon:var(--color-white)]',
    ],
  },
};

type ButtonProps = {
  color?: keyof typeof styles.colors;
  outline?: boolean;
  plain?: boolean;
  tinted?: boolean;
  glow?: boolean;
  className?: string;
  children?: React.ReactNode;
  size?: 'sm' | 'lg';
  loading?: boolean;
} & (
  | ({ href?: never } & Omit<Headless.ButtonProps, 'as' | 'className'>)
  | ({ href: string } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)
);

export const Button = forwardRef(function Button(
  {
    color,
    outline,
    plain,
    tinted,
    glow,
    className,
    children,
    size,
    loading,
    ...props
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  let classes = clsx(
    className,
    styles.base,
    size ? styles.sizes[size] : styles.sizes.default,
    outline
      ? styles.outline
      : plain
        ? styles.plain
        : tinted
          ? clsx(styles.tinted, styles.tintedColors[color ?? 'dark'])
          : clsx(styles.solid, styles.colors[color ?? 'dark']),
    // Glow modifier: hide the CSS border so only the SVG trace is visible
    glow && 'border-transparent',
    loading && 'pointer-events-none'
  );

  const inner = loading ? (
    <TouchTarget>
      <span className="absolute inset-0 flex items-center justify-center">
        <LoadingSpinner
          size={size === 'sm' ? 'xs' : size === 'lg' ? 'md' : 'sm'}
          variant="dots"
          className="text-current"
        />
      </span>
      <span className="inline-flex items-center gap-x-2 opacity-0">{children}</span>
    </TouchTarget>
  ) : (
    <TouchTarget>{children}</TouchTarget>
  );

  const element =
    typeof props.href === 'string' ? (
      <Link {...props} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
        {inner}
      </Link>
    ) : (
      <Headless.Button {...props} className={classes} ref={ref}>
        {inner}
      </Headless.Button>
    );

  if (glow) {
    return <GlowWrapper color={color}>{element}</GlowWrapper>;
  }

  return element;
});

// Single element with a conic-gradient mask rotating via @property. The mask
// reveals a bright head and a long fade-in tail behind it. One element, one
// CSS animation, GPU-friendly.
export const GLOW_BORDER_MASK =
  'conic-gradient(from var(--glow-angle), black 0%, black 3%, transparent 14%, transparent 50%, black 100%)';

function GlowWrapper({
  children,
  color = 'primary',
}: {
  children: React.ReactNode;
  color?: keyof typeof styles.colors;
}) {
  const glowColor = GLOW_COLORS[color ?? 'primary'] ?? GLOW_COLORS.primary;

  return (
    <span className="relative inline-flex">
      {children}
      <span
        className="absolute inset-0 rounded-sm pointer-events-none z-10 animate-glow-border"
        style={{
          border: `1px solid ${glowColor}`,
          maskImage: GLOW_BORDER_MASK,
          WebkitMaskImage: GLOW_BORDER_MASK,
        }}
        aria-hidden="true"
      />
    </span>
  );
}

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}
